/**
 * What a saved filter means: the words it is read back in, the rows it keeps, the
 * counts beside the tags and the days it groups them into.
 *
 * Every function here is pure — a `List<Bookmark>` and a [WindowContent.BookmarkList] in,
 * an answer out. No DOM, no clock reached for, no repository. That is not tidiness; it is
 * the reason this file exists at all rather than living in `:web` beside the markup it
 * paints.
 *
 * ── Filtering is client-side, and that is a decision ─────────────────────────
 *
 * The window already fetches the whole collection — `StorageRepository.bookmarks` says
 * why, and it is not laziness: the collection is written from *outside* the app, so there
 * is no cache to invalidate and every list window asks the server afresh. Given that it
 * is holding all of it anyway, filtering, counting, sorting and grouping happen **here**,
 * in the browser, over the list it already has.
 *
 * Three things follow, and the second is the one worth having:
 *
 *  1. A knob costs nothing. Changing the sort, the grouping or the column count is a
 *     recomputation over a list in memory rather than a round trip, so the window
 *     responds on the same frame and a filter can be dragged around without a spinner.
 *  2. **The rail's counts are live against the current result set, not the collection.**
 *     `#automation` reads 25 in an unfiltered window and 9 inside a filter, because 9 is
 *     how many of *these* rows carry it — which is the number somebody narrowing a search
 *     actually wants, and the one a `GET /api/tags` count can never be. It looks like a
 *     bug the first time you see it, so the expanded rail says so in words.
 *  3. It is honest at one person's scale and no further. A collection large enough that
 *     "all of it" stops being a thing a browser should hold is a collection that wants
 *     `GET /api/bookmarks` to grow a query and a page — and that is a *field* on
 *     `BookmarkList`, the wrapper that exists for exactly this, rather than a new response
 *     type or a second route. The day it happens, the functions here become the shape the
 *     server implements rather than dead code.
 *
 * ── And why the summary is one function ──────────────────────────────────────
 *
 * [filterSummary] is read in two places — the sidebar row's label, and the sentence the
 * expanded rail's footer reads the filter back in. One function, because two
 * implementations of "what is this window showing?" is two answers that drift, and the
 * first person to notice is somebody looking at a sidebar row that disagrees with the
 * footer under it.
 *
 * ── Where the filtering itself went, and what is left here ───────────────────
 *
 * The *semantics* — which rows a filter keeps, and in what order — moved down into
 * `:clientServer` as [BookmarkQuery] when LPN-58 gave them a second caller. An agent
 * asking `search_bookmarks` over `/mcp` and a person dragging a filter bar must mean the
 * same thing by "any of these tags" and by "newest", and two implementations of that agree
 * only until one of them is edited.
 *
 * So [filterBookmarks] below is now an adapter: it turns a saved window filter into a
 * [BookmarkQuery] and hands it to the one function that decides. The vocabularies are
 * aliases of the shared ones rather than copies — a `const val` whose value *is* the other
 * one, so they cannot drift and no Swift or Compose call site had to change. What is still
 * genuinely this file's is everything a *window* needs and a query does not: the summary
 * sentence, the tag rail's counts, and the day grouping.
 *
 * @see WindowContent.BookmarkList
 * @see BookmarkListBackingViewModel
 * @see se.soderbjorn.lunapin.clientserver.BookmarkQuery
 */
package se.soderbjorn.lunapin.client.viewmodel

import se.soderbjorn.lunapin.clientserver.Bookmark
import se.soderbjorn.lunapin.clientserver.BookmarkQuery
import se.soderbjorn.lunapin.clientserver.AddedWithins as SharedAddedWithins
import se.soderbjorn.lunapin.clientserver.AiSummaryFilters as SharedAiSummaryFilters
import se.soderbjorn.lunapin.clientserver.AiTagFilters as SharedAiTagFilters
import se.soderbjorn.lunapin.clientserver.BookmarkSorts as SharedBookmarkSorts
import se.soderbjorn.lunapin.clientserver.FilterMatches as SharedFilterMatches
import se.soderbjorn.lunapin.clientserver.filterBookmarks as filterByQuery
import se.soderbjorn.lunapin.clientserver.hostOf as sharedHostOf
import se.soderbjorn.lunapin.clientserver.sortBookmarks as sortByShared

// ── The vocabularies ─────────────────────────────────────────────────────────
//
// Strings rather than enums, for the reason `WindowContent.BookmarkList` states beside the
// fields that hold them: kotlinx-serialization refuses an unknown enum value, and an
// unparseable workspace blob costs the user every tab they had open. Every `when` over one
// of these therefore has an `else` that means "the default", and a value from a later
// client degrades to sensible rather than to nothing.
//
// Each constant below is defined *as* its counterpart in `:clientServer` rather than as a
// literal beside it. That is what keeps one implementation honest without breaking the
// three platforms that name these types: `BookmarkSorts.shared.all` is a Swift call site
// in `BookmarkFilterSheet.swift`, and moving the object out of `:client` would have been a
// rename nothing in this repository can compile.

/** How [WindowContent.BookmarkList.includeTags] combine. */
object FilterMatches {
    /** A bookmark must carry every included tag. The default: filters narrow. */
    const val ALL: String = SharedFilterMatches.ALL

    /** Any one included tag is enough. */
    const val ANY: String = SharedFilterMatches.ANY

    val all: List<String> = SharedFilterMatches.all
}

/** The order rows are shown in. */
object BookmarkSorts {
    /** Newest first — the server's own order, and the one a collection is added to at. */
    const val NEWEST: String = SharedBookmarkSorts.NEWEST

    /** Oldest first: the read-later pile, from the bottom. */
    const val OLDEST: String = SharedBookmarkSorts.OLDEST

    /** By title, A–Z, falling back to the URL for a bookmark that has none. */
    const val TITLE: String = SharedBookmarkSorts.TITLE

    val all: List<String> = SharedBookmarkSorts.all
}

/** Whether the list carries day headers. */
object BookmarkGroupings {
    /** One run of rows, no headers. */
    const val NONE: String = "none"

    /** A header per day the bookmarks below it were saved on. The default. */
    const val DAY: String = "day"

    val all: List<String> = listOf(NONE, DAY)
}

/**
 * How far back a bookmark may have been saved.
 *
 * **Rolling windows, not calendar buckets** — "the last 24 hours" rather than "today".
 * That is a deliberate simplification and it buys something real: a rolling window is
 * arithmetic on two `Long`s and needs no time zone, so the filter is the same answer in
 * every zone and there is no hour of the year where it disagrees with itself. Day
 * *grouping* is the place a zone genuinely cannot be avoided, and it takes one — see
 * [groupBookmarksByDay].
 */
object AddedWithins {
    const val ANY: String = SharedAddedWithins.ANY
    const val DAY: String = SharedAddedWithins.DAY
    const val WEEK: String = SharedAddedWithins.WEEK
    const val MONTH: String = SharedAddedWithins.MONTH
    const val YEAR: String = SharedAddedWithins.YEAR

    val all: List<String> = SharedAddedWithins.all

    /** How long each window is, or null for [ANY], which has no bound. */
    fun millis(value: String): Long? = SharedAddedWithins.millis(value)
}

// ── What the model has done to a row ─────────────────────────────────────────
//
// Two knobs rather than one combined *AI* select, and they compose. The interesting cell is
// the corner: "tagged, but never summarised" is a real triage state, and a single
// three-valued select cannot express it — `AI TAGS = Has tags` together with
// `AI SUMMARY = No summary` is one filter, in one window, rather than two windows compared
// by eye.
//
// **The predicates read the results, not the queue.** `aiTags.isNotEmpty()` and
// `aiSummary.isNotBlank()` — never `Bookmark.aiState`. The two disagree, and the
// disagreement is exactly the thing being filtered on: an enrichment that finished on a
// page the model had nothing to say about leaves `aiState = AiStates.DONE` with an empty
// `aiTags`, and the question *"has this row got AI tags?"* is answered by the tags on it,
// not by whether some worker got to the end of its job. Reaching for `AiStates` here is the
// natural mistake — it is the more prominent vocabulary and it is right next door on the
// wire — and it would quietly count that row as tagged.
//
// `AiStates` is for the knob these are **not**: a queue knob offering *pending / running /
// failed*, to find the rows that gave up and show `aiError` (LPN-55). That is a plausible
// third knob and a different question, deliberately out of scope here. Keep the two apart
// rather than folding a queue state into either of these.

/**
 * Whether a bookmark carries tags the model added.
 *
 * A re-export of [SharedAiTagFilters], since LPN-58 moved the filter down into
 * `:clientServer` — the reasoning, and the warning about reaching for `AiStates` instead,
 * live there now. Kept as a name in this package for the same reason the vocabularies above
 * are: three platforms name this type, and `AiTagFilters.shared.all` is a Swift call site.
 */
object AiTagFilters {
    /** Say nothing about it. The default. */
    const val ANY: String = SharedAiTagFilters.ANY

    /** Keep only rows the model put at least one tag on. */
    const val HAS: String = SharedAiTagFilters.HAS

    /** Keep only rows it put none on — including the ones it looked at and had none for. */
    const val MISSING: String = SharedAiTagFilters.MISSING

    val all: List<String> = SharedAiTagFilters.all

    /** Whether [bookmark] survives this knob at [value]. Unknown values are [ANY]. */
    fun keeps(bookmark: Bookmark, value: String): Boolean =
        SharedAiTagFilters.keeps(bookmark, value)
}

/**
 * Whether a bookmark carries a summary the model wrote.
 *
 * A re-export of [SharedAiSummaryFilters] — see [AiTagFilters]. It reads
 * [Bookmark.aiSummary] only, never [Bookmark.summary]: the user's own note is a different
 * field holding a different thing, and a hand-written note must not make a row look
 * summarised.
 */
object AiSummaryFilters {
    /** Say nothing about it. The default. */
    const val ANY: String = SharedAiSummaryFilters.ANY

    /** Keep only rows the model wrote a summary for. */
    const val HAS: String = SharedAiSummaryFilters.HAS

    /** Keep only rows it has not written one for — *what has the summariser not reached?* */
    const val MISSING: String = SharedAiSummaryFilters.MISSING

    val all: List<String> = SharedAiSummaryFilters.all

    /** Whether [bookmark] survives this knob at [value]. Unknown values are [ANY]. */
    fun keeps(bookmark: Bookmark, value: String): Boolean =
        SharedAiSummaryFilters.keeps(bookmark, value)
}

// ── Where the `ROWS` knob used to be ─────────────────────────────────────────
//
// `RowDensities` offered *Summaries* and *Compact*, and compact is gone. It was one line —
// title, domain, a couple of chips, the time — and the line was never long enough: the
// title shared it with everything else, so it was ellipsised at around thirty characters
// while the row was five hundred pixels wide, the chips beside it were clipped mid-word,
// and there was no summary at all. A list you still have to open a row to read is not a
// denser list, it is a shorter one.
//
// What is left is one row, and it is the card: the title takes the full width and wraps,
// the metadata sits under it, the tags wrap under that, and both the note and the model's
// summary show a couple of lines each. One shape means one set of rules about what gets
// the width, which is the whole reason the ellipsis could be dropped.
//
// The field is gone from `WindowContent.BookmarkList` too. A stored workspace still
// carrying `rowDensity` decodes exactly as before — `WorkspaceCodec` sets
// `ignoreUnknownKeys`, which is what it is for.

/** The column knob's values. */
object BookmarkColumns {
    /**
     * Pick from the available width, at roughly [MIN_CARD_PX] a card. The default, and the
     * right one: the same saved filter is one column in a narrow split and three when the
     * window is maximised, and nobody wants to re-pick a number every time they drag a
     * split bar.
     */
    const val AUTO: Int = 0

    /**
     * The narrowest a card may be squeezed to before the layout drops a column.
     *
     * **This is the number the title has to survive**, now that there is one row shape and
     * the promise about it is that you can read a bookmark's name without opening it. At
     * 340 a card gives the title around forty characters a line and four lines before it
     * gives up, which is a hundred and sixty — past all but the titles that are themselves
     * a paragraph. A laptop at full screen still resolves to three columns, which is what
     * this width is chosen to keep.
     */
    const val MIN_CARD_PX: Int = 340

    /** What [AUTO] resolves to at [widthPx]. At least one column, at most [MAX]. */
    fun resolve(columns: Int, widthPx: Int): Int = when {
        columns in 1..MAX -> columns
        else -> (widthPx / MIN_CARD_PX).coerceIn(1, MAX)
    }

    /**
     * Three, and no more.
     *
     * Not an arbitrary cap: a card is the summary, and past three columns in a window
     * anybody would call wide the measure is too short to read a sentence in. A fourth
     * column would be a grid of fragments.
     */
    const val MAX: Int = 3
}

// ── The window's name, in a few words ────────────────────────────────────────

/** Which of the two forms [filterSummary] renders. */
enum class FilterSummaryForm {
    /**
     * A sidebar row's worth: a couple of tags, then a count of what was left out.
     *
     * Truncation past that is the label's problem — the toolkit ellipsises a pane title
     * that does not fit, and a function that guessed at a pixel width would be guessing.
     */
    SHORT,

    /** The whole filter in plain words, for the footer that has room for it. */
    LONG,
}

/**
 * What this window is showing, in words.
 *
 * The one function behind two call sites: the sidebar label (via the shell's `paneLabel`)
 * and the sentence the expanded rail's footer reads the filter back in.
 *
 * **An unfiltered window is *All bookmarks***, in both forms. A number — "Window 3" — is
 * only a name when there is nothing better to say, and now there nearly always is.
 *
 * @param filter what the window holds.
 * @param form [FilterSummaryForm.SHORT] for a sidebar row, [FilterSummaryForm.LONG] for
 *   the footer.
 */
fun filterSummary(
    filter: WindowContent.BookmarkList,
    form: FilterSummaryForm = FilterSummaryForm.SHORT,
): String = when (form) {
    FilterSummaryForm.LONG -> longSummary(filter)
    FilterSummaryForm.SHORT -> shortSummary(filter)
}

/**
 * `#read-later and #ai, not #gaming, not #politics, added in the last 7 days`.
 *
 * Clauses joined by commas, with the included tags as one leading clause so that *and* and
 * *or* read as what they are — the [FilterMatches] rule — rather than as punctuation.
 */
private fun longSummary(filter: WindowContent.BookmarkList): String {
    val clauses = buildList {
        if (filter.includeTags.isNotEmpty()) {
            val joiner = if (filter.match == FilterMatches.ANY) " or " else " and "
            add(filter.includeTags.joinToString(joiner))
        }
        filter.excludeTags.forEach { add("not $it") }
        filter.query.trim().takeIf { it.isNotEmpty() }?.let { add("matching \"$it\"") }
        addedWithinPhrase(filter.addedWithin, long = true)?.let { add(it) }
        aiTagsPhrase(filter.aiTags, long = true)?.let { add(it) }
        aiSummaryPhrase(filter.aiSummary, long = true)?.let { add(it) }
    }
    return if (clauses.isEmpty()) UNFILTERED else clauses.joinToString(", ")
}

/**
 * `#read-later, #ai +2`.
 *
 * The first two things the filter says, then how many it did not have room for. Which two
 * is deliberate rather than arbitrary: the included tags lead, because a window filtered
 * to `#recipes` is *called* `#recipes`, and everything else is a qualifier on it.
 */
private fun shortSummary(filter: WindowContent.BookmarkList): String {
    val tokens = buildList {
        addAll(filter.includeTags)
        filter.excludeTags.forEach { add("not $it") }
        filter.query.trim().takeIf { it.isNotEmpty() }?.let { add("\"$it\"") }
        addedWithinPhrase(filter.addedWithin, long = false)?.let { add(it) }
        aiTagsPhrase(filter.aiTags, long = false)?.let { add(it) }
        aiSummaryPhrase(filter.aiSummary, long = false)?.let { add(it) }
    }
    return when {
        tokens.isEmpty() -> UNFILTERED
        tokens.size <= SHORT_TOKENS -> tokens.joinToString(", ")
        else -> tokens.take(SHORT_TOKENS).joinToString(", ") + " +${tokens.size - SHORT_TOKENS}"
    }
}

/** What an age window is called, or null for [AddedWithins.ANY], which says nothing. */
private fun addedWithinPhrase(addedWithin: String, long: Boolean): String? = when (addedWithin) {
    AddedWithins.DAY -> if (long) "added in the last 24 hours" else "24h"
    AddedWithins.WEEK -> if (long) "added in the last 7 days" else "7d"
    AddedWithins.MONTH -> if (long) "added in the last 30 days" else "30d"
    AddedWithins.YEAR -> if (long) "added in the last year" else "1y"
    else -> null
}

/**
 * What the `AI TAGS` knob is called, or null for [AiTagFilters.ANY], which says nothing.
 *
 * A knob that hides rows has to say so in words. Both forms of [filterSummary] feed one
 * label each — the sidebar pane and the expanded rail's footer sentence — and a window
 * silently holding back half its collection with nothing on screen to explain it is the
 * bug this is here to prevent.
 */
private fun aiTagsPhrase(value: String, long: Boolean): String? = when (value) {
    AiTagFilters.HAS -> if (long) "with AI tags" else "AI tags"
    AiTagFilters.MISSING -> if (long) "without AI tags" else "no AI tags"
    else -> null
}

/** What the `AI SUMMARY` knob is called, or null for [AiSummaryFilters.ANY]. */
private fun aiSummaryPhrase(value: String, long: Boolean): String? = when (value) {
    AiSummaryFilters.HAS -> if (long) "with an AI summary" else "AI summary"
    AiSummaryFilters.MISSING -> if (long) "without an AI summary" else "no AI summary"
    else -> null
}

/** What a window with nothing set is called. */
const val UNFILTERED: String = "All bookmarks"

/** How many things the short form names before it starts counting. */
private const val SHORT_TOKENS = 2

// ── Filtering ────────────────────────────────────────────────────────────────

/**
 * The rows [filter] keeps, in [WindowContent.BookmarkList.sort] order.
 *
 * **An adapter, since LPN-58.** A saved window filter is one shape a [BookmarkQuery] can
 * take — the shape a filter bar can express — so this turns one into the other and lets
 * the shared implementation decide. The behaviour is unchanged and the tests that pinned
 * it still pin it; what changed is that an agent asking the same question over `/mcp` now
 * gets the same answer by construction rather than by two files agreeing.
 *
 * The fields a window has no control for are left at their defaults, which all say
 * nothing: [BookmarkQuery.untagged], the absolute date bounds, `ai_state`, `has_summary`
 * and `host` narrow nothing until somebody asks for them. Paging is left alone too —
 * [filterByQuery] answers the whole match, and a window wants all of it.
 *
 * @param bookmarks the whole collection, as the server ordered it.
 * @param filter the window's saved filter.
 * @param now epoch millis, for [WindowContent.BookmarkList.addedWithin]. Passed rather
 *   than read, because shared code has no clock it can reach without an `expect`
 *   declaration — and because a function given the time is a function a test can pin.
 */
fun filterBookmarks(
    bookmarks: List<Bookmark>,
    filter: WindowContent.BookmarkList,
    now: Long,
): List<Bookmark> = filterByQuery(bookmarks, filter.asQuery(), now)

/**
 * The window's filter as the shared one.
 *
 * `internal` rather than private only because the list window's rail computes a "lifted"
 * count — the same filter with one exclusion removed — and does it by copying the window
 * filter rather than the query, so this is the one conversion and it happens here.
 */
internal fun WindowContent.BookmarkList.asQuery(): BookmarkQuery = BookmarkQuery(
    query = query,
    includeTags = includeTags,
    excludeTags = excludeTags,
    match = match,
    addedWithin = addedWithin,
    // The two AI knobs a window does have controls for, since LPN-61. The rest of
    // BookmarkQuery's AI surface — aiStates, hasSummary — has no control and stays at its
    // default, which says nothing.
    aiTags = aiTags,
    aiSummary = aiSummary,
    sort = sort,
)

/**
 * [bookmarks] in [sort] order.
 *
 * Delegates, for [filterBookmarks]'s reason. Kept as a name in this package because the
 * list window and the workspace miniatures both sort without filtering, and because
 * `sortBookmarks` is what the tests that guard the total order are written against.
 */
fun sortBookmarks(bookmarks: List<Bookmark>, sort: String): List<Bookmark> =
    sortByShared(bookmarks, sort)

/**
 * The host of [url] — what sits between the scheme and the first slash, without `www.`.
 *
 * Delegates to `:clientServer`'s, which is byte-for-byte what this function used to be.
 * The name stays here because it is the one the iOS framework exports —
 * `BookmarkFilterKt.hostOf` is a Swift call site in `SyncStatusView.swift` — and because
 * the two Compose and SwiftUI copies of it are still measured against this one.
 *
 * Those two platform copies are deliberately left where they are rather than deleted —
 * they are byte-identical, so nothing can drift today, and rewriting a Swift view for a
 * cleanup is a change to a file that cannot be driven on the build machine. Deleting them
 * is the natural first line of whatever touches those rows next.
 */
fun hostOf(url: String): String = sharedHostOf(url)

// ── The rail's counts ────────────────────────────────────────────────────────

/**
 * One chip in the tag rail.
 *
 * @property tag the tag, `#`-prefixed and lower-cased like every other.
 * @property uses **how many of the current result set carry it** — see the file header.
 *   Zero is an ordinary value here and means "nothing in this filter has it"; the expanded
 *   rail draws those dimmer still and *Hide empty* is what removes them.
 * @property fromAi whether every bookmark in the result set carrying this tag was tagged
 *   with it by the model rather than by hand — `TagUse.fromAi`'s rule, computed over the
 *   result set for the same reason [uses] is. Drawn as one small `AI` mark layered on
 *   whichever of the three states the chip is in, never as a fourth colour: three states
 *   times two origins is six treatments, and a mark is what keeps it at three.
 * @property latestAt the newest [Bookmark.createdAt] in the result set carrying it, or 0
 *   for a tag nothing here carries. What the rail's *Recent* order sorts on.
 * @property pinned whether this account nominated the tag in settings — *Offer by default*.
 *   **Not a fourth chip state and not a decoration**: it is a claim about where the chip
 *   goes and whether it is allowed to disappear. A pinned chip leads the rail whatever the
 *   sort, and it is drawn **even at zero uses**, including in the two-row collapsed rail
 *   that otherwise shows only tags the result set actually carries. Somebody who nominated
 *   `#read-later` wants to press it on the day nothing is filed under it — that is the day
 *   it is most useful, and a shortlist that vanishes when the shortlist is empty is not a
 *   shortlist. See [BookmarkListBackingViewModel]'s `rail`.
 */
data class RailTag(
    val tag: String,
    val uses: Int,
    val fromAi: Boolean,
    val latestAt: Long,
    val pinned: Boolean = false,
)

/**
 * Every tag worth a chip, counted against [result].
 *
 * @param result the rows the filter kept — **not** the collection. The whole point.
 * @param vocabulary every tag the account uses, from `GET /api/tags`, so a tag that
 *   nothing in this filter carries still gets a chip (at zero) in the expanded rail
 *   rather than vanishing the moment it would be most useful to click.
 * @param liftedCounts counts to use in place of the computed ones, by tag. The excluded
 *   tags go here: a tag this filter is *removing* has zero rows in the result set by
 *   construction, and a chip reading `#gaming 0` says nothing at all — so it is counted
 *   against the result set with its own exclusion lifted, which is the number a reader
 *   wants there ("this is holding back 12"). See
 *   [BookmarkListBackingViewModel] for where those are computed.
 * @param pinned the tags this account nominated in settings. A **third source of chips**
 *   beside the result set and the vocabulary, and the only one that can name a tag nothing
 *   has ever carried: somebody may nominate `#read-later` before filing anything under it,
 *   and the chip has to exist for that to be a shortcut rather than a note to self. See
 *   [RailTag.pinned] for what the flag then promises.
 */
fun railTags(
    result: List<Bookmark>,
    vocabulary: List<String>,
    liftedCounts: Map<String, Int> = emptyMap(),
    pinned: Set<String> = emptySet(),
): List<RailTag> {
    val counts = mutableMapOf<String, Int>()
    val byHand = mutableSetOf<String>()
    val latest = mutableMapOf<String, Long>()
    result.forEach { bookmark ->
        // The list, not a `Set` built from it. A bookmark's AI tags are a handful, and this
        // runs once per row of the result set on every keystroke — the set allocation cost
        // more than the linear scan it was there to avoid.
        val ai = bookmark.aiTags
        bookmark.tags.forEach { tag ->
            counts[tag] = (counts[tag] ?: 0) + 1
            if (tag !in ai) byHand += tag
            if ((latest[tag] ?: 0L) < bookmark.createdAt) latest[tag] = bookmark.createdAt
        }
    }
    return (counts.keys + vocabulary + liftedCounts.keys + pinned).distinct().map { tag ->
        RailTag(
            tag = tag,
            uses = liftedCounts[tag] ?: counts[tag] ?: 0,
            // A tag nothing here carries is nobody's: with no rows to look at there is no
            // evidence it was the model's, and marking it would be an assertion made out
            // of an empty set.
            fromAi = tag !in byHand && (counts[tag] ?: 0) > 0,
            latestAt = latest[tag] ?: 0L,
            pinned = tag in pinned,
        )
    }
}

/** How the expanded rail may be ordered. */
object RailSorts {
    /** Biggest first — the default, and what makes the two collapsed rows the useful ones. */
    const val COUNT: String = "count"

    /** A–Z, for finding a tag you can spell. */
    const val NAME: String = "name"

    /** Newest use first, for the tags this week's saving has been about. */
    const val RECENT: String = "recent"

    val all: List<String> = listOf(COUNT, NAME, RECENT)
}

/**
 * [tags] in [sort] order.
 *
 * Every comparator ends in the name, so the order is total and two chips do not swap
 * places between keystrokes — the same rule `tagMenu` obeys, for the same reason.
 */
fun sortRailTags(tags: List<RailTag>, sort: String): List<RailTag> = when (sort) {
    RailSorts.NAME -> tags.sortedBy { it.tag }
    RailSorts.RECENT -> tags.sortedWith(
        compareByDescending<RailTag> { it.latestAt }.thenBy { it.tag },
    )

    else -> tags.sortedWith(compareByDescending<RailTag> { it.uses }.thenBy { it.tag })
}

// ── Days ─────────────────────────────────────────────────────────────────────

/**
 * One day's worth of rows, under one header.
 *
 * @property dayStartMillis the instant local midnight began, in epoch millis. The platform
 *   formats it — the browser knows how the reader writes a date and a formatter in the
 *   bundle would be a second opinion about it.
 * @property daysAgo 0 for today, 1 for yesterday, and so on. What lets a header say
 *   *TODAY* without this file knowing any words.
 * @property bookmarks the rows, in the order the sort put them.
 */
data class BookmarkDayGroup(
    val dayStartMillis: Long,
    val daysAgo: Int,
    val bookmarks: List<Bookmark>,
)

/**
 * [bookmarks] cut into local days, **in the order they already appear**.
 *
 * Order of first appearance rather than newest-day-first, which matters exactly once: with
 * [BookmarkSorts.TITLE] the days come out in the order their first title does. That is
 * strange to describe and right to do — the alternative is a list whose headers say one
 * order while its rows say another, and grouping that reorders the sort is grouping that
 * silently overrides it.
 *
 * @param now epoch millis, for [BookmarkDayGroup.daysAgo].
 * @param zoneOffsetMillis the reader's offset from UTC, in millis — `Date.getTimezoneOffset`
 *   negated, on the web. **The one place a time zone cannot be avoided**: "which day was
 *   this saved on" has no answer without one, unlike the rolling windows [AddedWithins]
 *   uses. Taken as a parameter for the reason [filterBookmarks] takes [now]: shared code
 *   has no zone to reach for, and a function given one is a function a test can pin.
 *
 *   Read once, for the whole grouping, so it is the *current* offset applied to old rows —
 *   which is one hour wrong for bookmarks saved on the other side of a daylight-saving
 *   change. Knowingly: getting it right needs a full zone database, and the cost of being
 *   wrong is a bookmark saved at half past midnight appearing under the neighbouring day's
 *   header twice a year.
 */
fun groupBookmarksByDay(
    bookmarks: List<Bookmark>,
    now: Long,
    zoneOffsetMillis: Int,
): List<BookmarkDayGroup> {
    val today = localDayIndex(now, zoneOffsetMillis)
    val order = mutableListOf<Long>()
    val buckets = mutableMapOf<Long, MutableList<Bookmark>>()
    bookmarks.forEach { bookmark ->
        val day = localDayIndex(bookmark.createdAt, zoneOffsetMillis)
        if (day !in buckets) {
            order += day
            buckets[day] = mutableListOf()
        }
        buckets.getValue(day) += bookmark
    }
    return order.map { day ->
        BookmarkDayGroup(
            dayStartMillis = day * DAY_MILLIS - zoneOffsetMillis,
            daysAgo = (today - day).toInt(),
            bookmarks = buckets.getValue(day),
        )
    }
}

/** Which local day [millis] falls in, counted from the epoch. Negative before 1970. */
private fun localDayIndex(millis: Long, zoneOffsetMillis: Int): Long =
    floorDiv(millis + zoneOffsetMillis, DAY_MILLIS)

/**
 * `a / b` rounded towards negative infinity.
 *
 * Kotlin's `/` truncates towards zero, which puts every instant of 31 December 1969 in the
 * same day as 1 January 1970. Nobody's bookmark is from 1969 — and a date function that is
 * wrong for half its domain is one somebody will reuse somewhere it is not.
 */
private fun floorDiv(a: Long, b: Long): Long {
    val q = a / b
    return if (a % b != 0L && (a xor b) < 0) q - 1 else q
}

private const val DAY_MILLIS = 24L * 60 * 60 * 1000
